import React, { useMemo, useState } from 'react';

/**
 * Editable proposal table before Apply — mirrors bulk create fields.
 */
export default function OpsTemplateProposalPreview({
  applyPayload,
  onChange,
  knownWorkTypes = [],
  onContinueAnyway = null,
  showContinueAnyway = false
}) {
  const templates = Array.isArray(applyPayload?.templates) ? applyPayload.templates : [];
  const [collapsed, setCollapsed] = useState(false);

  const rows = useMemo(() => templates, [templates]);

  if (!applyPayload || !rows.length) return null;

  const templateType = applyPayload.templateType || 'common';
  const isAccommodation = templateType === 'independent' || templateType === 'group';

  const updateMeta = (patch) => {
    onChange?.({ ...applyPayload, ...patch });
  };

  const updateRow = (index, patch) => {
    const next = rows.map((row, i) => (i === index ? { ...row, ...patch } : row));
    onChange?.({ ...applyPayload, templates: next });
  };

  const removeRow = (index) => {
    const next = rows.filter((_, i) => i !== index);
    onChange?.({ ...applyPayload, templates: next });
  };

  const parseList = (value) =>
    String(value || '')
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);

  return (
    <div className="rounded-xl border border-teal-100 bg-teal-50/40 p-3">
      <div className="mb-2 flex items-center justify-between gap-2">
        <div>
          <p className="text-xs font-semibold text-teal-900">Proposal preview</p>
          <p className="text-[10px] text-teal-700">Edit before Apply</p>
        </div>
        <button
          type="button"
          className="text-[10px] font-semibold text-teal-800 underline"
          onClick={() => setCollapsed((c) => !c)}
        >
          {collapsed ? 'Expand' : 'Collapse'}
        </button>
      </div>

      {!collapsed ? (
        <>
          <div className="mb-2 grid grid-cols-2 gap-2">
            <label className="text-[10px] font-medium text-slate-600">
              Template name
              <input
                value={applyPayload.template_name || ''}
                onChange={(e) => updateMeta({ template_name: e.target.value })}
                className="mt-0.5 w-full rounded-lg border border-slate-200 bg-white px-2 py-1 text-xs"
              />
            </label>
            <label className="text-[10px] font-medium text-slate-600">
              Type
              <select
                value={applyPayload.templateType || 'common'}
                onChange={(e) =>
                  updateMeta({ templateType: e.target.value, templateTypeConfirmed: true })
                }
                className="mt-0.5 w-full rounded-lg border border-slate-200 bg-white px-2 py-1 text-xs"
              >
                <option value="common">Common</option>
                <option value="independent">Independent</option>
                <option value="group">Group</option>
              </select>
            </label>
          </div>

          <div className="max-h-56 space-y-2 overflow-y-auto">
            {rows.map((row, index) => (
              <div
                key={`${row.name}-${index}`}
                className="rounded-lg border border-white bg-white/80 p-2 shadow-sm"
              >
                <div className="mb-1 flex items-center justify-between gap-2">
                  <input
                    value={row.name || ''}
                    onChange={(e) => updateRow(index, { name: e.target.value })}
                    className="flex-1 rounded border border-slate-150 bg-white px-2 py-1 text-xs font-medium"
                    placeholder="Task name"
                  />
                  <button
                    type="button"
                    onClick={() => removeRow(index)}
                    className="text-[10px] text-red-600 hover:underline"
                  >
                    Remove
                  </button>
                </div>
                <div className="grid grid-cols-3 gap-1.5">
                  <label className="text-[9px] text-slate-500">
                    Days
                    <input
                      type="number"
                      min={1}
                      value={row.duration ?? 1}
                      onChange={(e) =>
                        updateRow(index, {
                          duration: Math.max(1, Number(e.target.value) || 1)
                        })
                      }
                      className="mt-0.5 w-full rounded border border-slate-200 px-1.5 py-0.5 text-xs"
                    />
                  </label>
                  <label className="text-[9px] text-slate-500">
                    Idle
                    <input
                      type="number"
                      min={0}
                      value={row.delay ?? 0}
                      onChange={(e) =>
                        updateRow(index, {
                          delay: Math.max(0, Number(e.target.value) || 0)
                        })
                      }
                      className="mt-0.5 w-full rounded border border-slate-200 px-1.5 py-0.5 text-xs"
                    />
                  </label>
                  <label className="flex items-end gap-1 pb-1 text-[9px] text-slate-500">
                    <input
                      type="checkbox"
                      checked={Boolean(row.drawing_required)}
                      onChange={(e) => updateRow(index, { drawing_required: e.target.checked })}
                    />
                    Drawing
                  </label>
                </div>
                {isAccommodation ? (
                  <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[9px] text-slate-500">
                    <label className="flex items-center gap-1">
                      <input
                        type="checkbox"
                        checked={Boolean(row.floorwise)}
                        onChange={(e) =>
                          updateRow(index, {
                            floorwise: e.target.checked,
                            working_order: e.target.checked ? row.working_order || null : null
                          })
                        }
                      />
                      Every floor
                    </label>
                    {row.floorwise ? (
                      <select
                        value={row.working_order || ''}
                        onChange={(e) => updateRow(index, { working_order: e.target.value || null })}
                        className={`rounded border px-1 py-0.5 text-[10px] ${
                          row.working_order ? 'border-slate-200' : 'border-amber-400'
                        }`}
                      >
                        <option value="">Order…</option>
                        <option value="Bottom Up">Bottom Up</option>
                        <option value="Top Down">Top Down</option>
                      </select>
                    ) : null}
                    {templateType === 'group' ? (
                      <label className="flex items-center gap-1">
                        <input
                          type="checkbox"
                          checked={Boolean(row.unitwise)}
                          onChange={(e) => updateRow(index, { unitwise: e.target.checked })}
                        />
                        Each flat
                      </label>
                    ) : null}
                  </div>
                ) : null}
                <label className="mt-1 block text-[9px] text-slate-500">
                  Work type
                  <input
                    list={`ops-work-types-${index}`}
                    value={row.workTypeName || ''}
                    onChange={(e) => updateRow(index, { workTypeName: e.target.value })}
                    className="mt-0.5 w-full rounded border border-slate-200 px-1.5 py-0.5 text-xs"
                    placeholder="e.g. Civil"
                  />
                  {knownWorkTypes.length ? (
                    <datalist id={`ops-work-types-${index}`}>
                      {knownWorkTypes.map((name) => (
                        <option key={name} value={name} />
                      ))}
                    </datalist>
                  ) : null}
                </label>
                <label className="mt-1 block text-[9px] text-slate-500">
                  Dependencies (comma-separated
                  {isAccommodation && row.floorwise ? '; own name first = previous floor' : ''})
                  <input
                    value={(row.dependency || []).join(', ')}
                    onChange={(e) => updateRow(index, { dependency: parseList(e.target.value) })}
                    className="mt-0.5 w-full rounded border border-slate-200 px-1.5 py-0.5 text-xs"
                  />
                </label>
                <label className="mt-1 block text-[9px] text-slate-500">
                  Sub-activities (comma-separated)
                  <input
                    value={(row.subActivity || []).join(', ')}
                    onChange={(e) => updateRow(index, { subActivity: parseList(e.target.value) })}
                    className="mt-0.5 w-full rounded border border-slate-200 px-1.5 py-0.5 text-xs"
                  />
                </label>
              </div>
            ))}
          </div>
        </>
      ) : (
        <p className="text-xs text-teal-800">
          {rows.length} task(s) · {applyPayload.template_name || 'Untitled'} ·{' '}
          {templateType}
        </p>
      )}

      {showContinueAnyway && onContinueAnyway ? (
        <button
          type="button"
          onClick={onContinueAnyway}
          className="mt-2 text-[10px] font-semibold text-slate-600 underline"
        >
          Continue anyway (skip remaining questions)
        </button>
      ) : null}
    </div>
  );
}
